import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "../services/authService";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { useToast } from "../components/ui/ToastContainer";
import { parseApiError } from "../utils/errorHandler";
import pmLogo from "../assets/logo.png";
import { Lock, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();
  const token = searchParams.get("token");

  const [formData, setFormData] = useState({
    password: "",
    passwordConfirm: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isInvalidToken, setIsInvalidToken] = useState(false);

  useEffect(() => {
    if (!token) {
      setIsInvalidToken(true);
      setError("Invalid or missing reset token. Please request a new password reset.");
    }
  }, [token]);

  const getPasswordStrength = (password: string) => {
    if (!password) return { score: 0, label: "", color: "" };
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    const labels = ["Very Weak", "Weak", "Fair", "Good", "Strong"];
    const colors = ["bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-green-500", "bg-emerald-600"];

    return { score, label: labels[score - 1] || "", color: colors[score - 1] || "" };
  };

  const strength = getPasswordStrength(formData.password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long");
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.passwordConfirm) {
      setError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    try {
      const response = await authService.resetPassword(
        token,
        formData.password,
        formData.passwordConfirm
      );

      if (response && response.status === "success") {
        setIsSuccess(true);
        showSuccess("Password reset successful!", "Redirecting to login...");
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        const msg = "Unexpected response from server";
        setError(msg);
        showError(msg, "Reset Failed");
      }
    } catch (err: any) {
      console.error("Reset password error:", err);
      const apiError = parseApiError(err);
      setError(apiError.message);
      if (apiError.message.includes("expired") || apiError.message.includes("invalid")) {
        setIsInvalidToken(true);
      }
      showError(apiError.message, "Reset Failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToForgot = () => {
    navigate("/forgot-password");
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  if (isInvalidToken) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 p-3">
              <img
                src={pmLogo}
                alt="AH Planning"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-[#102040] tracking-tight">
              Invalid or Expired Link
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              This password reset link is no longer valid.
            </p>
          </div>

          <Card className="p-8 shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center">
                Password reset links expire after 30 minutes for security. If you
                still need to reset your password, please request a new one.
              </p>

              <div className="space-y-3 pt-4">
                <Button
                  variant="primary"
                  onClick={handleBackToForgot}
                  className="w-full h-11 text-base font-medium shadow-md shadow-[#f09040]/20 hover:shadow-[#f09040]/40 transition-all"
                >
                  Request New Reset Link
                </Button>

                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="inline-flex items-center justify-center gap-2 text-sm font-medium text-gray-600 hover:text-[#f09040] transition-colors w-full"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 p-3">
            <img
              src={pmLogo}
              alt="AH Planning"
              className="w-full h-full object-contain"
            />
          </div>
          <h2 className="text-2xl font-bold text-[#102040] tracking-tight">
            Set New Password
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Create a strong password for your account
          </p>
        </div>

        <Card className="p-8 shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          {isSuccess ? (
            <div className="text-center py-8">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold text-[#102040] mb-2">
                Password Reset Successful
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Your password has been successfully reset. You can now log in
                with your new password.
              </p>
              <p className="text-xs text-gray-500">
                Redirecting to login page...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-100 rounded-lg p-3 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600 font-medium">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          password: e.target.value,
                        });
                        if (error) setError(null);
                      }}
                      placeholder="••••••••"
                      className="pl-10"
                      disabled={isLoading}
                    />
                  </div>
                  {formData.password && (
                    <div className="mt-2">
                      <div className="flex gap-1 h-1 mb-1">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div
                            key={i}
                            className={`flex-1 rounded-full transition-colors ${
                              i <= strength.score ? strength.color : "bg-gray-200"
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-xs text-gray-500">
                        Password strength:{" "}
                        <span
                          className={`font-medium ${
                            strength.score >= 3 ? "text-green-600" : "text-gray-600"
                          }`}
                        >
                          {strength.label || "Enter password"}
                        </span>
                      </p>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Must be at least 8 characters long
                  </p>
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="passwordConfirm"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="passwordConfirm"
                      name="passwordConfirm"
                      type="password"
                      value={formData.passwordConfirm}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          passwordConfirm: e.target.value,
                        });
                        if (error) setError(null);
                      }}
                      placeholder="••••••••"
                      className="pl-10"
                      disabled={isLoading}
                    />
                  </div>
                  {formData.passwordConfirm && formData.password !== formData.passwordConfirm && (
                    <p className="text-xs text-red-500">Passwords do not match</p>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                className="w-full h-11 text-base font-medium shadow-md shadow-[#f09040]/20 hover:shadow-[#f09040]/40 transition-all"
                disabled={
                  isLoading ||
                  !formData.password ||
                  !formData.passwordConfirm ||
                  formData.password.length < 8 ||
                  formData.password !== formData.passwordConfirm
                }
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#f09040] transition-colors"
                  disabled={isLoading}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </button>
              </div>
            </form>
          )}
        </Card>

        <p className="text-center text-sm text-gray-500">
          © {new Date().getFullYear()} AH Planning. All rights reserved.
        </p>
      </div>
    </div>
  );
}
